import React, { memo } from 'react';
import dynamic from 'next/dynamic';
import { gql } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { GetStaticPropsContext } from 'next/types';
import getCitySlugAction from '@/lib/getCitySlugAction';
import { hasRestaurantMenu } from '@/lib/restaurantMenu';
import globalState from '@/lib/globalState';
import { initializeApollo } from '../lib/apollo';
import { ADMIN_URL } from '../lib/env';
import { AppState, defaultContext, type GlobalState } from '../components/context/Silverstripe';
import { fetchAdditionalPageData, fetchPage, fetchRedirect } from '../lib/pageBuild';
import { buildData } from '../data/buildData';
import SiteLayout from '../components/layouts/SiteLayout';
import LoadingLayout from '../components/layouts/LoadingLayout';
import { RedirectPagePropTypes } from '../components/templates/RedirectPage';

dayjs.extend(utc);
dayjs.extend(timezone);

const Restaurant = dynamic(() => import('../components/templates/Restaurant'), {
  loading: () => <LoadingLayout />,
});
const OurStoryPage = dynamic(() => import('../components/templates/OurStoryPage'), {
  loading: () => <LoadingLayout />,
});
const City = dynamic(() => import('../components/templates/City'), {
  loading: () => <LoadingLayout />,
});
const CitySiteLayout = dynamic(() => import('../components/layouts/CitySiteLayout'), {
  loading: () => <LoadingLayout />,
});
const HomePage = dynamic(() => import('../components/templates/HomePage'), {
  loading: () => <LoadingLayout />,
});
const RestaurateursPage = dynamic(() => import('../components/templates/RestaurateursPage'), {
  loading: () => <LoadingLayout />,
});
const FAQPage = dynamic(() => import('../components/templates/FAQPage'), {
  loading: () => <LoadingLayout />,
});
const SideMenuLayout = dynamic(() => import('../components/templates/SideMenuLayout'), {
  loading: () => <LoadingLayout />,
});
const FullWidthPage = dynamic(() => import('../components/templates/FullWidthPage'), {
  loading: () => <LoadingLayout />,
});
const IntegrationsPage = dynamic(() => import('../components/templates/IntegrationsPage'), {
  loading: () => <LoadingLayout />,
});
const IntegrationPage = dynamic(() => import('../components/templates/IntegrationPage'), {
  loading: () => <LoadingLayout />,
});
const Blog = dynamic(() => import('../components/templates/Blog'), {
  loading: () => <LoadingLayout />,
});
const CompetitionsHome = dynamic(() => import('../components/templates/CompetitionsHome'), {
  loading: () => <LoadingLayout />,
});
const BlogPost = dynamic(() => import('../components/templates/BlogPost'), {
  loading: () => <LoadingLayout />,
});
const ContactPage = dynamic(() => import('../components/templates/ContactPage'), {
  loading: () => <LoadingLayout />,
});
const UserDefinedFormPage = dynamic(() => import('../components/templates/UserDefinedFormPage'), {
  loading: () => <LoadingLayout />,
});
const UserDefinedFormTwoColumnPage = dynamic(
  () => import('../components/templates/UserDefinedFormTwoColumnPage'),
  {
    loading: () => <LoadingLayout />,
  }
);
const CompetitionPage = dynamic(() => import('../components/templates/CompetitionPage'), {
  loading: () => <LoadingLayout />,
});
const FirsTablePromoCodePage = dynamic(
  () => import('../components/templates/FirsTablePromoCodePage'),
  {
    loading: () => <LoadingLayout />,
  }
);
const CityComingSoon = dynamic(() => import('../components/templates/CityComingSoon'), {
  loading: () => <LoadingLayout />,
});
const StoriesPage = dynamic(() => import('../components/templates/StoriesPage'), {
  loading: () => <LoadingLayout />,
});
const JobsHomePage = dynamic(() => import('../components/templates/JobsHomePage'), {
  loading: () => <LoadingLayout />,
});
const JobPage = dynamic(() => import('../components/templates/JobPage'), {
  loading: () => <LoadingLayout />,
});
const SuggestRestaurantPage = dynamic(
  () => import('../components/templates/SuggestRestaurantPage'),
  {
    loading: () => <LoadingLayout />,
  }
);

const RedirectPage = dynamic(() => import('../components/templates/RedirectPage'), {
  loading: () => <LoadingLayout />,
});

/**
 * Resolves the page template name (e.g. City, Restaurant) from page data and global state.
 * Used for Layout selection in PageResolver and for New Relic error context in getStaticProps.
 */
function resolvePageTemplate(
  page: {
    className?: string;
    pageAction?: string;
    slug?: string;
    children?: { edges?: unknown[] };
    parent?: unknown;
    frontendTemplate?: string;
    comingSoon?: boolean;
    parentId?: number | null;
    showOnFrontend?: boolean;
    id?: number;
  },
  preview: boolean,
  global: GlobalState,
  force: boolean
): string | undefined {
  if (page.className === 'Restaurant' && page.pageAction === 'menu') return 'RestaurantMenu';
  let { className } = page;

  if (page.slug === '/our-story') {
    className = 'OurStory';
  }
  if (page.slug === '/partners') {
    className = 'Partners';
  }
  if (
    (className === 'Page' && ((page.children?.edges?.length ?? 0) > 0 || page.parent)) ||
    page.frontendTemplate === 'Side Menu'
  ) {
    className = 'SideMenuPage';
  }
  if (page.slug === '/privacy-policy' || page.slug === '/covid-19') {
    className = 'FullWidthPage';
  }
  if (className === 'UserDefinedForm' && page.slug === '/contact-us') {
    className = 'ContactPage';
  }
  if (className === 'UserDefinedForm') {
    className = 'UserDefinedFormPage';
  }
  if (page.slug === '/first-table-promo-code') {
    className = 'FirsTablePromoCodePage';
  }
  if (page.slug === '/our-community/suggest-a-restaurant') {
    className = 'SuggestRestaurantPage';
  }

  if (page.frontendTemplate === 'Integrations Home') {
    className = 'IntegrationsPage';
  }
  if (page.frontendTemplate === 'Integration') {
    className = 'IntegrationPage';
  }
  if (page.frontendTemplate === 'Stories') {
    className = 'StoriesPage';
  }
  if (page.frontendTemplate === 'Competitions Home') {
    className = 'CompetitionsHome';
  }
  if (page.frontendTemplate === 'Form Default') {
    className = 'UserDefinedFormPage';
  }
  if (page.frontendTemplate === 'Form Two Column') {
    className = 'UserDefinedFormTwoColumnPage';
  }
  if (page.frontendTemplate === 'Form Contact Us') {
    className = 'ContactPage';
  }
  if (page.frontendTemplate === 'Jobs Home') {
    className = 'JobsHomePage';
  }
  if (page.frontendTemplate === 'Job') {
    className = 'JobPage';
  }
  if (page.frontendTemplate === 'Restaurant Landing Page') {
    className = 'RestaurantLandingPage';
  }

  const countryStates = global?.countryStates ?? [];
  const liveCities = (countryStates as { cities?: { id: number }[] }[])
    .map((node) => (node.cities ?? []).map((city) => city.id))
    .reduce((arr: number[], val) => arr.concat(val), []);

  if (
    !force &&
    className === 'City' &&
    (page.comingSoon ||
      (page.parentId == null && !preview && !liveCities.includes(page.id!) && page.showOnFrontend))
  ) {
    className = 'CityComingSoon';
  }

  return className;
}

const handleRedirect = (redirect: RedirectPagePropTypes, payload: any) => {
  const { to, from, permanent } = redirect;

  if (to === from) {
    return {
      notFound: true,
    };
  }

  if (process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD && to) {
    return {
      redirect: {
        destination: to,
        permanent: permanent || false,
      },
    };
  }
  return {
    props: {
      ...payload,
      redirect,
    },
    revalidate: 60,
  };
};

// Memoised so shallow filter navigation does not rebuild the page state for every consumer.
const PageResolver = memo(({ globalData, global, ...state }: any) => {
  const newState: AppState = {
    ...defaultContext,
    global: { ...defaultContext.global, ...globalData, ...global },
    ...state,
  };
  const { page, preview, redirect, force } = newState;

  if (redirect) {
    return (
      <SiteLayout appState={newState} showMenu={false} {...newState}>
        <RedirectPage redirect={redirect} />
      </SiteLayout>
    );
  }

  const className = resolvePageTemplate(page, preview ?? false, newState.global, force ?? false);

  let Layout;
  let showMenu = true;
  switch (className) {
    case 'HomePage':
      Layout = HomePage;
      showMenu = false;
      break;
    case 'VirtualPage':
      Layout = SideMenuLayout;
      break;
    case 'Page':
      Layout = FullWidthPage;
      break;
    case 'ContactPage':
      Layout = ContactPage;
      break;
    case 'UserDefinedForm':
      Layout = FullWidthPage;
      break;
    case 'UserDefinedFormPage':
      Layout = UserDefinedFormPage;
      break;
    case 'Blog':
      Layout = Blog;
      break;
    case 'BlogPost':
      Layout = BlogPost;
      break;
    case 'FAQPage':
      Layout = FAQPage;
      break;
    case 'RestaurantMenu':
      Layout = Restaurant;
      break;
    case 'Restaurant':
      Layout = Restaurant;
      break;
    case 'City':
      Layout = City;
      break;
    case 'CityComingSoon':
      Layout = CityComingSoon;
      break;
    case 'RestaurantLandingPage':
      Layout = RestaurateursPage;
      showMenu = false;
      break;
    case 'CompetitionsHome':
      Layout = CompetitionsHome;
      break;
    case 'Competition':
      Layout = CompetitionPage;
      break;
    case 'OurStory':
      Layout = OurStoryPage;
      break;
    case 'StoriesPage':
      Layout = StoriesPage;
      break;
    case 'SideMenuPage':
      Layout = SideMenuLayout;
      break;
    case 'FullWidthPage':
      Layout = FullWidthPage;
      break;
    case 'IntegrationsPage':
      Layout = IntegrationsPage;
      showMenu = false;
      break;
    case 'IntegrationPage':
      Layout = IntegrationPage;
      break;
    case 'FirsTablePromoCodePage':
      Layout = FirsTablePromoCodePage;
      break;
    case 'UserDefinedFormTwoColumnPage':
      Layout = UserDefinedFormTwoColumnPage;
      break;
    case 'JobsHomePage':
      Layout = JobsHomePage;
      break;
    case 'JobPage':
      Layout = JobPage;
      break;
    case 'SuggestRestaurantPage':
      Layout = SuggestRestaurantPage;
      break;
    default:
      Layout = FullWidthPage;
      break;
  }

  const PageLayout = className === 'City' ? CitySiteLayout : SiteLayout;

  return (
    <PageLayout appState={newState} showMenu={showMenu} {...newState}>
      <Layout page={page} />
    </PageLayout>
  );
});
PageResolver.displayName = 'PageResolver';
export default PageResolver;

const stripTrailingEndingSlashes = (slug) => {
  if (!slug || slug === '/') {
    return slug;
  }

  return slug.split('/').filter(Boolean).join('/');
};

export async function getStaticProps({
  params: { slug },
  preview = false,
  revalidateReason,
}: GetStaticPropsContext) {
  const byPassCache = revalidateReason === 'on-demand';
  let slugParts = slug || [];
  let pageAction = '';
  let pageActionValue = '';

  // Uniform slug array
  if (!Array.isArray(slugParts)) {
    slugParts = `${slugParts}`.split('/').filter(Boolean);
  }

  // if(slugParts.slice(-1)[0].includes('HASH-')) {
  //   const hash = slugParts.pop();
  //   // decode hash into props - AB tests, personalisation
  // }
  let force = false;
  if (['!'].includes(slugParts.slice(-1)[0])) {
    slugParts.pop();
    force = true;
  }

  const citySlug = getCitySlugAction(slugParts);
  slugParts = citySlug.slugParts;
  pageAction = citySlug.pageAction;
  pageActionValue = citySlug.pageActionValue;

  // Handle [magazine]/topic/[food] routes
  if (slugParts.slice(-2)[0] === 'topic') {
    pageActionValue = slugParts.pop();
    pageAction = slugParts.pop();
  }

  if (['manage', 'admin'].includes(slugParts[0])) {
    return {
      redirect: {
        destination: `${ADMIN_URL}${slugParts.join('/')}`,
        permanent: true,
      },
    };
  }

  // if (['GmailCRM', 'social', 'sales', 'review'].includes(slugParts[0])) {
  //   return {
  //     redirect: {
  //       destination: `${CMS_URL}${slugPath}`,
  //       permanent: true,
  //     },
  //   };
  // }

  let slugPath = `/${slugParts.join('/')}`;
  let isRestaurantMenu = false;

  // If slug path contains a dot (likely a filename), return not found
  if (slugPath.includes('.')) {
    return {
      notFound: true,
    };
  }

  let cmsPage = await fetchPage(slugPath, pageAction, pageActionValue, preview, byPassCache);
  if (
    slugParts.length > 1 &&
    slugParts.at(-1) === 'menu' &&
    (!cmsPage.data.page ||
      (cmsPage.data.page.className === 'Restaurant' && cmsPage.data.page.slug !== slugPath))
  ) {
    const parentSlug = `/${slugParts.slice(0, -1).join('/')}`;
    const parentPage = await fetchPage(parentSlug, '', '', preview, byPassCache);
    if (parentPage.data.page?.className === 'Restaurant') {
      cmsPage = parentPage;
      slugPath = parentSlug;
      isRestaurantMenu = true;
    }
  }

  // Don't render VirtualPages
  if (cmsPage.data.page?.className === 'VirtualPage') {
    return {
      notFound: true,
    };
  }

  if (
    pageAction === 'cuisine' &&
    (cmsPage.data.page?.className !== 'City' ||
      !`/${(Array.isArray(slug) ? slug.join('/') : slug) || ''}`.endsWith(
        `/cuisine/${pageActionValue}`
      ))
  )
    return { notFound: true, revalidate: 3600 };

  const actualCMSSlugToCompare = stripTrailingEndingSlashes(cmsPage.data.page?.slug);
  const slugPathToCompare = stripTrailingEndingSlashes(slugPath);

  // Handles aliases and redirection pages i.e. /blue-kanu -> /queenstown-lakes/queenstown/blue-kanu
  if (actualCMSSlugToCompare && slugPathToCompare && slugPathToCompare !== actualCMSSlugToCompare) {
    let suffix = isRestaurantMenu ? '/menu' : '';
    if (pageAction === 'cuisine') suffix = `/cuisine/${pageActionValue}`;
    return handleRedirect(
      {
        to: `${cmsPage.data.page.slug}${suffix}`,
        from: slugPath,
      },
      cmsPage
    );
  }

  const {
    data: { page },
    error,
  } = cmsPage;

  if (error || !page) {
    // look for Silverstripe history/old versions of pages
    const {
      data: { map },
    } = await fetchRedirect(slugPath, preview);
    if (map?.destination) {
      return handleRedirect({ to: map.destination, from: slugPath }, cmsPage);
    }

    // else doesn't exist
    return {
      notFound: true,
    };
  }

  // Return notFound if ShowOnFrontend is false
  if (!page.showOnFrontend && !force) {
    return {
      notFound: true,
    };
  }

  // get any additional data that we can't know ahead of time relating to the page template being requested
  const staticProps = await fetchAdditionalPageData(
    cmsPage.data,
    preview,
    byPassCache,
    null,
    pageAction,
    pageActionValue
  );

  if (
    !staticProps.page
    // || (templateSession && !staticProps.page.restaurantsBySession[templateSession])
  ) {
    return {
      notFound: true,
    };
  }

  if (staticProps.page?.redirect) {
    staticProps.page.className = 'RedirectPage';
    return handleRedirect(staticProps.page.redirect, staticProps.page);

    if (process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD) {
      return { redirect: { destination: staticProps.page.redirect.to, permanent: false } };
    }
  }

  if (page.className === 'City') {
    if (pageAction === 'cuisine' && !staticProps.page.cuisineLanding) {
      return { notFound: true, revalidate: 3600 };
    }
    staticProps.page.sessions =
      staticProps?.page?.features
        ?.filter((i) => ['Breakfast', 'Lunch', 'Dinner', 'LastTable'].includes(i))
        ?.map((feature) => feature.toLowerCase()) || [];

    const { sessions } = staticProps.page;

    // No sessions for the city
    if (!staticProps.page.comingSoon && !sessions.length && !force) {
      return {
        notFound: true,
      };
    }

    // If page is a city, check whether dinner restaurants exist
    // If not, temporarily redirect to /lunch or /breakfast
    if (process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD && ['', 'session'].includes(pageAction)) {
      const invalidSession =
        sessions.length &&
        !sessions.includes(pageActionValue.replace('dinner2', 'lasttable') || 'dinner');
      if (invalidSession) {
        const destination = sessions.find((s) => s === 'dinner') ? '' : sessions[0];

        if (!destination) {
          return {
            notFound: true,
          };
        }

        return {
          redirect: {
            destination: `${page.slug}/${destination}`,
            permanent: false,
          },
        };
      }
    }
  }

  if (isRestaurantMenu) {
    if (!hasRestaurantMenu(staticProps.page)) return { notFound: true, revalidate: 3600 };
    pageAction = 'menu';
    staticProps.page.pageAction = 'menu';
  }

  // ISR regenerate this page in seconds
  let revalidate;
  switch (page.className) {
    case 'City': {
      const cityTimezone = staticProps.page.timeZone;
      const currentDate = dayjs().tz(cityTimezone);
      const currentHour = parseInt(currentDate.format('H'));

      // calculate the number of seconds from currentHour to 6am
      const secondsUntil = (hour) => {
        let difference = hour - currentHour;
        if (difference < 0) {
          difference += 24;
        }
        return difference * 60 * 60;
      };

      switch (pageActionValue) {
        case 'breakfast':
          revalidate = currentHour >= 6 && currentHour < 10 ? 20 * 60 : secondsUntil(6);
          break;
        case 'lunch':
          revalidate = currentHour >= 10 && currentHour < 14 ? 20 * 60 : secondsUntil(10);
          break;
        case 'dinner':
        default:
          revalidate = currentHour >= 14 && currentHour < 21 ? 20 * 60 : secondsUntil(14);
          break;
      }
      break;
    }
    default:
      revalidate = 24 * 60 * 60;
      break;
  }

  const pageTemplate = resolvePageTemplate(staticProps.page, preview, globalState, force);
  // Keep the list/store mounted when refreshing CMS data for suburbs in the same region.
  const isCityList = pageTemplate === 'City';

  return {
    props: {
      ...staticProps,
      page: {
        ...staticProps.page,
        className: staticProps.page.className,
        pageAction,
        pageActionValue,
      },
      pageTemplate,
      key: [
        page.className,
        isCityList ? staticProps.page.regionId || page.id : page.id,
        isRestaurantMenu || isCityList ? '' : pageAction,
        isCityList ? '' : pageActionValue,
      ]
        .filter(Boolean)
        .join('-'),
      preview,
      force,
    },
    revalidate,
  };
}

export const PAGES_QUERY = gql`
  query pagesQuery($siteId: Int!) {
    pages: allPages(siteId: $siteId, buildFilters: [LIVERESTAURANTS]) {
      id
      className
      slug
      virtualChildrenPages {
        slug
      }
    }
  }
`;

type Page = {
  id?: number;
  parentId?: number;
  className: string;
  slug: string;
  virtualChildrenPages: {
    className: string;
    slug: string;
  }[];
};

export async function getStaticPaths() {
  const apolloClient = initializeApollo();
  const env = process.env.NODE_ENV;

  let pages: Page[] = [];
  if (env === 'production') {
    if (process.env.USE_MOCK_BUILD_DATA) {
      pages = buildData.data.pages;
    } else {
      const { data } = await apolloClient.query({
        query: PAGES_QUERY,
        variables: {
          siteId: process.env.NEXT_PUBLIC_SITE_ID,
        },
      });
      pages = data.pages;
    }
  }

  // Quick build doesn't pre-render restaurants, suburbs, cities, etc.
  if (
    process.env.VERCEL_GIT_COMMIT_REF === 'release/quick' ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'release/quick' ||
    process.env.VERCEL_GIT_COMMIT_MESSAGE === "Merge branch 'release/quick'" ||
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_MESSAGE === "Merge branch 'release/quick'"
  ) {
    pages = pages.filter((page) => page.slug === '/');
  }

  pages = pages.filter(
    // cleanse the data e.g. we dont want gift-voucher/purchase as it conflicts with next page
    (node) => !['RestaurantList', 'RedirectorPage', 'ControllerPage'].includes(node.className)
  );

  const virtualPages: Page[] = [];
  // Add in virtual children pages
  pages.forEach((page) => {
    page.virtualChildrenPages.forEach((virtualPage) =>
      virtualPages.push({ ...virtualPage, parentId: page.id, virtualChildrenPages: [] })
    );
  });
  pages = [...pages, ...virtualPages];

  return {
    paths: pages.map((node) => ({
      params: {
        slug: node.slug.split('/').filter(Boolean),
      },
    })),
    fallback: 'blocking', // await ssr and don't show a loading page
  };
}
